// Source-map helpers — pure primitives shared between the admin editor
// and the backend chunker / estimator.
//
// Everything here is deterministic, dependency-free, and character-based
// (no tokenizer, no locale awareness). The goal is that the admin can
// preview values (live token badges, derived split patterns) that agree
// exactly with what the backend will persist and use at chunking time —
// same input in, same value out, no round trip.
//
// Callers:
//   - packages/api/src/shared/ingestion/chunk-size-estimator.ts
//   - packages/api/src/shared/ingestion/chunker.ts
//   - packages/admin/src/source-documents/* (client-side live recompute)

// ─── Token estimation ─────────────────────────────────────────────────────

/**
 * Character-to-token ratio used across every estimator in the system.
 * ~4 chars/token holds for French + English prose within ±10%; the
 * badge thresholds (green/amber/red) are set with that band in mind.
 * A real tokenizer would tighten this but adds a runtime dep in a hot
 * path that runs on every keystroke; not worth it until a specific
 * decision demands better precision.
 */
export const CHARS_PER_TOKEN = 4;

/**
 * Rough token count for a string. Trims whitespace first so
 * indentation and trailing newlines don't inflate the count.
 * Never negative; empty string → 0.
 */
export function estimateTokens(text: string): number {
  return Math.round(text.trim().length / CHARS_PER_TOKEN);
}

// ─── Header-pattern derivation ────────────────────────────────────────────

/** Max characters of body text we'll use as a derived header pattern.
 *  Longer than this and the pattern gets over-specific — a stray
 *  whitespace difference between authoring and chunking will miss the
 *  match. Six words are usually well under this cap; the ceiling is a
 *  safety belt for very long words / no-space languages. */
const DERIVED_PATTERN_MAX_CHARS = 200;

/** Number of leading words we take from the body text when deriving a
 *  pattern. Chosen to be distinctive enough to disambiguate paragraphs
 *  within a chapter without being so long that a stray edit breaks the
 *  match. See header-match.ts for the flexible-whitespace matching that
 *  makes short patterns robust. */
const DERIVED_PATTERN_MAX_WORDS = 6;

/**
 * Derive a header_pattern from the first few words of some body text.
 * Used when the reviewer creates a new sub-section (from a gap or from
 * a split) and hasn't typed a heading yet — we take the paragraph's
 * opening so the chunker can re-match it at ingest time.
 *
 * Not guaranteed unique within a chapter. Two paragraphs opening with
 * the same words (Olivier's book has several "Il faut…" openings) will
 * both match; the chunker's findLastMatch picks the last occurrence.
 * MR 7 surfaces this as a warning; a robust fix (offset-based anchors)
 * is deferred until we see collisions in the wild.
 *
 * Deterministic and pure — the admin uses this to preview what the
 * persisted pattern will look like, and the persisted value goes to
 * the chunker verbatim.
 */
export function deriveHeaderPatternFromText(text: string): string {
  const words = text.trim().split(/\s+/).slice(0, DERIVED_PATTERN_MAX_WORDS);
  return words.join(' ').slice(0, DERIVED_PATTERN_MAX_CHARS);
}
